import { useEffect, useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import Pagination from "@/components/base/Pagination";
import FilterPanel from "@/pages/category/components/FilterPanel";
import type { ArticleCategory } from "@/mocks/articles";
import {
  PAGE_SIZE,
  getArticlesByCategory,
  getCategoryMeta,
  getTags,
  getYears,
} from "@/utils/articles";

interface CategoryPageProps {
  category: ArticleCategory;
}

export default function CategoryPage({ category }: CategoryPageProps) {
  const meta = getCategoryMeta(category)!;
  const base = useMemo(() => getArticlesByCategory(category), [category]);
  const tags = useMemo(() => getTags(category), [category]);
  const years = useMemo(() => getYears(category), [category]);

  const [activeTag, setActiveTag] = useState("all");
  const [activeYear, setActiveYear] = useState<number | "all">("all");
  const [sort, setSort] = useState<"date" | "hot">("date");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 350);
    return () => clearTimeout(t);
  }, [activeTag, activeYear, sort, page, category]);

  const filtered = useMemo(() => {
    let list = base.filter((a) => {
      const tagOk = activeTag === "all" || a.tags.includes(activeTag);
      const yearOk = activeYear === "all" || a.date.startsWith(String(activeYear));
      return tagOk && yearOk;
    });
    list = [...list].sort((a, b) =>
      sort === "hot"
        ? b.views + b.favorites * 5 - (a.views + a.favorites * 5)
        : a.date < b.date
          ? 1
          : -1,
    );
    return list;
  }, [base, activeTag, activeYear, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [activeTag, activeYear, sort]);

  const reset = () => {
    setActiveTag("all");
    setActiveYear("all");
    setSort("date");
    setPage(1);
  };

  const filterProps = {
    tags,
    years,
    activeTag,
    activeYear,
    sort,
    resultCount: filtered.length,
    onTagChange: setActiveTag,
    onYearChange: setActiveYear,
    onSortChange: setSort,
    onReset: reset,
  };

  return (
    <SiteLayout>
      <div className="bg-primary-900 text-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: meta.name }]} />
          <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-xs tracking-[0.3em] text-accent-400 mb-3 uppercase">
                {meta.enName}
              </p>
              <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4">{meta.name}</h1>
              <p className="text-sm md:text-base text-background-200 leading-relaxed max-w-3xl">
                {meta.intro}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-heading text-4xl font-bold text-accent-400">{base.length}</p>
              <p className="text-xs text-background-300 mt-1">篇文章</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24 bg-background-50 border border-background-200 rounded-lg p-6">
              <FilterPanel {...filterProps} />
            </div>
          </aside>

          <div className="lg:col-span-9">
            <div className="lg:hidden mb-5">
              <button
                onClick={() => setDrawerOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-filter-3-line" />
                筛选与排序
                <span className="ml-1 text-xs text-foreground-400">({filtered.length})</span>
              </button>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="rounded-lg border border-background-200 overflow-hidden">
                    <div className="w-full aspect-[8/5] bg-background-100 animate-pulse" />
                    <div className="p-5 space-y-3">
                      <div className="h-3 w-20 bg-background-100 rounded animate-pulse" />
                      <div className="h-4 w-full bg-background-100 rounded animate-pulse" />
                      <div className="h-3 w-2/3 bg-background-100 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : pageItems.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-background-300 rounded-lg">
                <i className="ri-file-search-line text-5xl text-foreground-300" />
                <p className="mt-4 text-foreground-600">无符合条件的文章</p>
                <button
                  onClick={reset}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-secondary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line" />
                  重置筛选
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                  {pageItems.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40 animate-fade-in"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-2xl max-h-[85vh] overflow-y-auto animate-fade-up">
            <div className="sticky top-0 bg-background-50 flex items-center justify-between px-5 py-4 border-b border-background-200">
              <h3 className="font-heading font-bold text-foreground-950">筛选与排序</h3>
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭筛选"
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-5 pb-8">
              <FilterPanel
                {...filterProps}
                onTagChange={(t) => {
                  setActiveTag(t);
                }}
                onYearChange={(y) => {
                  setActiveYear(y);
                }}
                onSortChange={(s) => {
                  setSort(s);
                }}
              />
              <button
                onClick={() => setDrawerOpen(false)}
                className="mt-6 w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                查看 {filtered.length} 篇文章
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}